import { jsx as _jsx } from "react/jsx-runtime";
import { Box } from '@mui/material';
import { useTheme } from '@mui/system';
import { Tooltip } from 'components/custom';
import { DragAndDropContext } from 'components/custom/DragAndDropProvider';
import React from 'react';
const isOperationAllowed = (droppableOperationName, draggableOperationName = []) => {
    const droppableOps = typeof droppableOperationName === 'string'
        ? [droppableOperationName]
        : droppableOperationName;
    const draggableOps = typeof draggableOperationName === 'string'
        ? [draggableOperationName]
        : draggableOperationName;
    return droppableOps.some((drop) => draggableOps.includes(drop));
};
const Droppable = ({ children, droppableId, droppableItemName, height = '100%', operationName, onFileDrop, outlineColor = 'primary', outlinePosition, positionLeft = 0, positionTop = 0, tooltipTitle, width = '100%', zIndex = 0, }) => {
    const { dragAndDropContextData, setDragAndDropContextData } = React.useContext(DragAndDropContext);
    const droppableRef = React.useRef(null);
    const { pointerX, pointerY, droppableId: contextDroppableId, operationName: contextOperationName, } = dragAndDropContextData;
    const [isHovered, setIsHovered] = React.useState(false);
    const checkHover = React.useCallback((x = 0, y = 0) => {
        const droppableTop = droppableRef.current?.offsetTop ?? 0;
        const droppableBottom = droppableTop + (droppableRef.current?.offsetHeight ?? 0);
        const droppableLeft = droppableRef.current?.offsetLeft ?? 0;
        const droppableRight = droppableLeft + (droppableRef.current?.offsetWidth ?? 0);
        return (x > droppableLeft + positionLeft &&
            x < droppableRight + positionLeft &&
            y > droppableTop + positionTop &&
            y < droppableBottom + positionTop);
    }, [positionLeft, positionTop]);
    React.useEffect(() => {
        if (contextDroppableId !== droppableId) {
            setIsHovered(false);
        }
        if (contextDroppableId !== droppableId &&
            isOperationAllowed(operationName, contextOperationName) &&
            checkHover(pointerX, pointerY)) {
            setDragAndDropContextData((prevState) => ({
                ...prevState,
                droppableId,
                droppableItemName,
            }));
            setIsHovered(true);
        }
        if (contextDroppableId === droppableId && !checkHover(pointerX, pointerY)) {
            setDragAndDropContextData((prevState) => ({
                ...prevState,
                droppableId: null,
                droppableItemName: '',
            }));
            setIsHovered(false);
        }
        if (pointerX === undefined || pointerY === undefined) {
            setIsHovered(false);
        }
    }, [
        checkHover,
        pointerX,
        pointerY,
        operationName,
        contextOperationName,
        setDragAndDropContextData,
        droppableId,
        contextDroppableId,
        droppableItemName,
    ]);
    const theme = useTheme();
    const getOutlineColor = () => {
        if (outlineColor === 'white') {
            return theme.palette.common.white;
        }
        return theme.palette[outlineColor].main;
    };
    const outlineWidth = theme.spacing(theme.spacingFactors.half);
    const outlineOffset = outlinePosition === 'inside' ? `-${outlineWidth}` : 0;
    const onDrop = (e) => {
        e.preventDefault();
        onFileDrop?.(e);
        setIsHovered(false);
    };
    const onDragOver = (e) => {
        e.preventDefault();
        if (!isHovered) {
            setIsHovered(true);
        }
    };
    return (_jsx(Box, { ref: droppableRef, sx: {
            height,
            outlineColor: isHovered ? getOutlineColor() : 'transparent',
            outlineOffset,
            outlineStyle: 'solid',
            outlineWidth,
            overflow: 'hidden',
            width,
            zIndex,
        }, ...(onFileDrop && {
            onDragLeave: () => {
                setIsHovered(false);
            },
            onDragOver,
            onDrop,
        }), children: tooltipTitle ? (_jsx(Tooltip, { open: isHovered, title: tooltipTitle, placement: "top", children: _jsx(Box, { children: children }) })) : (children) }));
};
export default Droppable;
